import { computed, readonly, ref, type Ref } from 'vue'

/**
 * Colour theme preference.
 *
 * `system` follows the OS and reacts live to it; `light` and `dark` pin the
 * app regardless. The preference is resolved to a concrete theme and written
 * to `<html data-theme>`, which is what every token in tokens.css keys off.
 *
 * The same storage key is read by the inline boot script in index.html so the
 * first paint is already correct — keep the two in sync.
 */
export type ThemePreference = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'settings:theme'

const DARK_QUERY = '(prefers-color-scheme: dark)'
const TRANSITION_MS = 220

function isPreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark'
}

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return isPreference(stored) ? stored : 'system'
  } catch {
    // Storage unavailable (private mode) — the preference stays session-only.
    return 'system'
  }
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

// Module-level so every caller of useTheme() shares one source of truth.
const preference: Ref<ThemePreference> = ref('system')
const resolved: Ref<ResolvedTheme> = ref('dark')
let initialised = false

function apply(theme: ResolvedTheme) {
  resolved.value = theme
  const root = document.documentElement
  root.dataset.theme = theme

  // Browser/PWA chrome should match the app bars, not the page canvas.
  const barColor = getComputedStyle(root).getPropertyValue('--app-browser-theme-color').trim()
  if (barColor) {
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', barColor)
  }
}

let transitionTimer: ReturnType<typeof setTimeout> | undefined

function applyWithTransition(theme: ResolvedTheme) {
  if (theme === resolved.value && document.documentElement.dataset.theme === theme) return
  const root = document.documentElement
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    root.dataset.themeSwitching = ''
    clearTimeout(transitionTimer)
    transitionTimer = setTimeout(() => delete root.dataset.themeSwitching, TRANSITION_MS)
  }
  apply(theme)
}

/**
 * Resolve and apply the stored preference, and start following the OS.
 * Called once from main.ts before the app mounts; safe to call again.
 */
export function initTheme() {
  if (initialised) return
  initialised = true

  preference.value = readStoredPreference()
  apply(preference.value === 'system' ? systemTheme() : preference.value)

  window.matchMedia(DARK_QUERY).addEventListener('change', () => {
    if (preference.value === 'system') applyWithTransition(systemTheme())
  })
}

export function setThemePreference(next: ThemePreference) {
  preference.value = next
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next)
  } catch {
    // Storage unavailable — the choice still applies for this session.
  }
  applyWithTransition(next === 'system' ? systemTheme() : next)
}

export function useTheme() {
  return {
    /** What the user picked: 'system' | 'light' | 'dark'. */
    preference: readonly(preference),
    /** What is actually rendered: 'light' | 'dark'. */
    resolved: readonly(resolved),
    isDark: computed(() => resolved.value === 'dark'),
    setPreference: setThemePreference,
  }
}
